import { useEffect, useRef } from 'react'

interface ReCaptchaProps {
  onChange: (token: string | null) => void
  onError?: () => void
}

declare global {
  interface Window {
    grecaptcha: any
    onRecaptchaLoad?: () => void
  }
}

export default function ReCaptcha({ onChange, onError }: ReCaptchaProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<number | null>(null)
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY

  useEffect(() => {
    // Skip if no site key configured
    if (!siteKey) {
      console.warn('reCAPTCHA site key not configured')
      return
    }

    // Load reCAPTCHA script
    const scriptId = 'recaptcha-script'
    let script = document.getElementById(scriptId) as HTMLScriptElement

    if (!script) {
      script = document.createElement('script')
      script.id = scriptId
      script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit'
      script.async = true
      script.defer = true
      document.head.appendChild(script)
    }

    // Render reCAPTCHA widget
    const renderRecaptcha = () => {
      if (!containerRef.current || widgetIdRef.current !== null) return

      if (window.grecaptcha && window.grecaptcha.render) {
        try {
          widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token: string) => {
              onChange(token)
            },
            'expired-callback': () => {
              onChange(null)
            },
            'error-callback': () => {
              onChange(null)
              onError?.()
            },
          })
        } catch (error) {
          console.error('Error rendering reCAPTCHA:', error)
        }
      }
    }

    // Set up callback for when script loads
    window.onRecaptchaLoad = renderRecaptcha

    // If script already loaded, render immediately
    if (window.grecaptcha?.render) {
      renderRecaptcha()
    }

    // Cleanup
    return () => {
      if (widgetIdRef.current !== null && window.grecaptcha?.reset) {
        try {
          window.grecaptcha.reset(widgetIdRef.current)
        } catch (error) {
          // Ignore cleanup errors
        }
        widgetIdRef.current = null
      }
    }
  }, [siteKey, onChange, onError])

  // Don't render anything if no site key
  if (!siteKey) {
    return null
  }

  return <div ref={containerRef} className="flex justify-center my-4" />
}
